import { Box, Paper, Popper, Typography, Skeleton, Link, ClickAwayListener } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";

import { SearchResult } from "@/api/generated.schemas";
import { NavigationItem } from "@/utils/navigationSearchItems";
import { SearchResultGroup } from "./SearchResultGroup";

interface SearchDropdownProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  navigationResults: NavigationItem[];
  apiResults: SearchResult[];
  total: number;
  isLoading: boolean;
  selectedIndex: number;
  onSelect: (index: number) => void;
  onClose: () => void;
  onNavigate: (path: string) => void;
  query: string;
}

/**
 * Loading skeleton for search results.
 */
function SearchLoadingSkeleton() {
  return (
    <Box sx={{ p: 2 }}>
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Empty state when no results found.
 */
function SearchEmptyState({ query }: { query: string }) {
  return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        No results found for "{query}"
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
        Try searching for goals, conversations, transcripts, or resources
      </Typography>
    </Box>
  );
}

/**
 * Search results dropdown component.
 */
export function SearchDropdown({
  open,
  anchorEl,
  navigationResults,
  apiResults,
  total,
  isLoading,
  selectedIndex,
  onSelect,
  onClose,
  onNavigate,
  query,
}: SearchDropdownProps) {
  const navigate = useNavigate();

  // Group API results by type
  const groupedApiResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    for (const result of apiResults) {
      const type = result.type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(result);
    }
    return groups;
  }, [apiResults]);

  // Calculate category order
  const categories = useMemo(() => {
    const cats: { category: string; results: (SearchResult | NavigationItem)[] }[] = [];

    // Navigation first (instant results)
    if (navigationResults.length > 0) {
      cats.push({
        category: "navigation",
        results: navigationResults,
      });
    }

    // Then API results by type
    const typeOrder = ["goal", "conversation", "transcript", "resource", "course", "enrollment"];
    for (const type of typeOrder) {
      const results = groupedApiResults[type];
      if (results && results.length > 0) {
        cats.push({
          category: type,
          results,
        });
      }
    }

    return cats;
  }, [navigationResults, groupedApiResults]);

  // Handle navigation to a result
  const handleNavigateToResult = (index: number) => {
    let currentIndex = 0;
    for (const cat of categories) {
      for (const result of cat.results) {
        if (currentIndex === index) {
          const path = "path" in result ? result.path : result.url;
          onNavigate(path);
          navigate(path);
          return;
        }
        currentIndex++;
      }
    }
  };

  const hasResults = navigationResults.length > 0 || apiResults.length > 0;
  const showEmptyState = !isLoading && query.length >= 2 && !hasResults;

  if (!open || !anchorEl) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorEl.offsetWidth, minWidth: 300, maxWidth: 500 }}
      >
        <Paper
          elevation={8}
          sx={{
            mt: 0.5,
            maxHeight: 400,
            overflow: "auto",
            borderRadius: 2,
          }}
        >
          {/* Loading state */}
          {isLoading && <SearchLoadingSkeleton />}

          {/* Empty state */}
          {showEmptyState && <SearchEmptyState query={query} />}

          {/* Results */}
          {!isLoading && hasResults && (
            <>
              {categories.map((cat, catIndex) => {
                // Calculate start index for this group
                let groupStartIdx = 0;
                for (let i = 0; i < catIndex; i++) {
                  groupStartIdx += categories[i].results.length;
                }

                return (
                  <SearchResultGroup
                    key={cat.category}
                    category={cat.category}
                    results={cat.results}
                    selectedIndex={selectedIndex}
                    groupStartIndex={groupStartIdx}
                    onSelect={(index) => {
                      onSelect(index);
                      handleNavigateToResult(index);
                    }}
                    query={query}
                  />
                );
              })}

              {/* See all results link */}
              {total > navigationResults.length + apiResults.length && (
                <Box
                  sx={{
                    p: 1.5,
                    borderTop: 1,
                    borderColor: "divider",
                    textAlign: "center",
                  }}
                >
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      navigate(`/search?q=${encodeURIComponent(query)}`);
                      onClose();
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    See all {total} results
                  </Link>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Popper>
    </ClickAwayListener>
  );
}
